import { useEffect, useState, useCallback, useRef } from "react"
import { Box, Typography, Grid, IconButton, Slider } from "@mui/material"
import Hls from "hls.js"
import api from "../services/api"
import { API_ENDPOINTS } from "../config/api"
import { Fullscreen, Pause, PlayArrow, Replay, VolumeOff, VolumeUp } from "@mui/icons-material"
import { useLocation } from "react-router-dom";

const VideoPlayer = ({ src, id }) => {
  const location = useLocation();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [videoError, setVideoError] = useState(null);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (!Hls.isSupported() && !video.canPlayType("application/vnd.apple.mpegurl")) {
      console.log("Your browser does not support live video streaming.")
      return
    }
    setVideoError(null)

    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy()
      }

      hlsRef.current = new Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000,
        liveSyncDurationCount: 15,
        liveMaxLatencyDurationCount: 30,
        liveDurationInfinity: true,
      })

      hlsRef.current.loadSource(src)
      hlsRef.current.attachMedia(video)

      hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsPlaying(false)
      })

      hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setVideoError(data?.error?.message)
              console.warn("HLS network error - trying to recover")
              hlsRef.current.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              setVideoError(data?.error?.message)
              console.warn("HLS media error - trying to recover")
              hlsRef.current.recoverMediaError()
              break
            default:
              setVideoError(data?.error?.message)
              console.error("Unrecoverable HLS error:", data)
              break
          }
        }
      })
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src
    }
    video.addEventListener("playing", () => {
      setIsPlaying(true);
      setVideoError(null)
      if (hlsRef.current && hlsRef.current.liveSyncPosition !== null) {
        const liveEdge = hlsRef.current.liveSyncPosition;
        const current = video.currentTime;
      
        if (Math.abs(current - liveEdge) > 5) {
          video.currentTime = liveEdge;
        } 
      }
    });
  
    video.addEventListener("pause", () => {
      setIsPlaying(false);
    });
  
    video.addEventListener("ended", () => {
      setIsPlaying(false);
    });

    video.addEventListener("volumechange", () => {
      setIsMuted(video.muted);
    })
  }, [src]);

  useEffect(() => {
    playVideo();
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
      }
    };
  }, [playVideo, location.pathname]);

  return (
    <Box
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        overflow: 'hidden',
        '&:hover .video-controls': {
          opacity: 1,
        },
      }}
    >
      <video
        ref={videoRef}
        id={id}
        style={{
          background: "black",
          width: "100%",
          height: "100%",
          objectFit: "contain",
          borderRadius: 8,
          display: "block",     // Avoid default inline spacing
        }}
        autoPlay
        muted
        playsInline
        pip="true"
        disablePictureInPicture={false}
        // onError={handleVideoError}
        controls={false}  // Disable native controls
      />
      {/* Custom Controls - Updated styles for fullscreen */}
      <Box
        className="video-controls"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(0, 0, 0, 0.7)',  // More transparent background
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          height: '40px',  // Slightly taller for better visibility
          borderRadius: '0px 0px 8px 8px',
          '&:hover': {
            opacity: 1,
          },
          // Show controls in fullscreen
          ':fullscreen &': {
            opacity: 1,
          },
          ':-webkit-full-screen &': {
            opacity: 1,
          },
          ':-moz-full-screen &': {
            opacity: 1,
          },
          ':-ms-fullscreen &': {
            opacity: 1,
          },
        }}
      >
        {/* Play/Pause */}
        <IconButton
          onClick={() => {
            if (!videoError && videoRef.current.paused) {
              if (hlsRef.current && hlsRef.current.liveSyncPosition) {
                videoRef.current.currentTime = hlsRef.current.liveSyncPosition - 3;
              }
              videoRef.current.play();
            } else if (!videoError) {
              videoRef.current.pause();
            } else if (videoError) {
              playVideo();
            }
          }}
          sx={{ color: 'white', p: 0.5 }}
        >
          { 
          videoError ? <Replay fontSize="small" /> : 
          !isPlaying ? <PlayArrow fontSize="small" /> : <Pause fontSize="small" />
          }
        </IconButton>

        {/* Live Tag */}
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'white', fontWeight: 'bold', fontSize: 14 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'red', mr: 0.5 }} />
          LIVE
        </Box>

        {/* Volume Control */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            '&:hover .volume-slider': {
              opacity: 1,
              width: 60, // smoothly expand
            },
          }}
        >
          <IconButton 
            onClick={() => {
              videoRef.current.muted = !videoRef.current.muted;
            }}
            sx={{ color: 'white', p: 0.5 }}
          >
            {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
          </IconButton>
          
          <Slider
            className="volume-slider"
            size="small"
            defaultValue={100}
            onChange={(_, value) => {
              videoRef.current.volume = value / 100;
            }}
            sx={{
              color: 'white',
              opacity: 0,
              width: 0,
              ml: 1,
              transition: 'opacity 0.3s ease, width 0.3s ease',
              '& .MuiSlider-thumb': {
                width: 10,
                height: 10,
              },
            }}
          />

          { videoError && <Typography sx={{ color: 'white', fontSize: 14 }}>{videoError}</Typography>}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Fullscreen */}
        <IconButton 
          onClick={() => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              videoRef.current.requestFullscreen();
            }
          }}
          sx={{ color: 'white', p: 0.5 }}
        >
          <Fullscreen fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const CCTV = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [videos, setVideos] = useState([])

  const getGridSize = (count) => {
    if (count === 1) return 12;
    else if (count === 2) return 6;
    else if (count >= 3) return 6;
    return 6; // untuk 4 kamera, 2 kolom x 2 baris
  };

  const fetchCCTVSensors = useCallback(async () => {
    try {
      let sensorsResponse = await api.get(`${API_ENDPOINTS.SENSOR.GET_ALL}?filter=${encodeURIComponent(JSON.stringify({"url": { "$regex": "^http"}}))}&isPreview=true`)
      if (sensorsResponse.data.totalItems > 10) {
        sensorsResponse = await api.get(`${API_ENDPOINTS.SENSOR.GET_ALL}?limit=${sensorsResponse.data.totalItems}&filter=${encodeURIComponent(JSON.stringify({"url": { "$regex": "^http"}}))}&isPreview=true`)
      }
      const cctvSensors = sensorsResponse.data.sensors

      setIsLoading(false)
      setVideos(cctvSensors)
      return cctvSensors
    } catch (err) {
      console.error("Error fetching CCTV sensor:", err)
      setIsLoading(false)
      throw new Error(err)
    }
  }, [setVideos, setIsLoading])

  useEffect(() => {
    fetchCCTVSensors();
  }, [fetchCCTVSensors])

  return (
    <Box sx={{ width: "100%", height: "calc(100vh - 164px)", pl: 1, pr: 2, py: 0, borderRadius: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
        CCTV
      </Typography>
      {isLoading ? <Typography>Loading...</Typography> : (
        <Grid container spacing={1} sx={{ top: 0, left: 0, right: 0, bottom: 0, borderRadius: 8  }}>
        {videos.length === 0 && <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>No CCTV found</Grid>}
        {videos.map((video) => (
          <Grid item xs={12} sm={getGridSize(videos.length)} key={video.id}>
            <VideoPlayer src={video.url} id={video.id}></VideoPlayer>
          </Grid>
        ))}
        </Grid>
      )}
    </Box>
  )
}

export default CCTV
